import {
  Autocomplete,
  TextField,
  type AutocompleteRenderInputParams,
} from "@mui/material";
import { useCallback, type SyntheticEvent } from "react";
import { useOutboundBenchDriversQuery } from "../../../generated/graphql";
import { type BenchDriverOption } from "../bench-run";

const EMPTY_DRIVERS: BenchDriverOption[] = [];

const HELPER_TEXT =
  "Routing assigns the bench route to this driver, and starting the route needs one.";

const driverLabel = (option: BenchDriverOption): string => option.displayName;

const isSameDriver = (option: BenchDriverOption, value: BenchDriverOption) =>
  option.uuid === value.uuid;

type BenchDriverPickerProps = {
  readonly companyUuid: string | null;
  readonly value: BenchDriverOption | null;
  readonly onChange: (value: BenchDriverOption | null) => void;
};

/**
 * Picks the active driver of the partnership's company that the bench routes
 * its stops to. The operator chooses; nothing is preselected.
 */
const BenchDriverPicker = ({
  companyUuid,
  value,
  onChange,
}: BenchDriverPickerProps) => {
  // The internal app's watch queries default to `errorPolicy: "ignore"`,
  // which would show a failed load as a company with no drivers.
  const { data, loading, error } = useOutboundBenchDriversQuery({
    errorPolicy: "none",
    skip: companyUuid === null,
    variables: { companyUuid: companyUuid ?? "" },
  });
  const drivers = data?.outboundBenchDrivers ?? EMPTY_DRIVERS;

  const handleChange = useCallback(
    (_: SyntheticEvent, next: BenchDriverOption | null) => {
      onChange(next);
    },
    [onChange],
  );

  const helperText =
    error === undefined
      ? HELPER_TEXT
      : `Could not load this company's drivers: ${error.message}`;
  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        error={error !== undefined}
        helperText={helperText}
        label="Test driver"
      />
    ),
    [error, helperText],
  );

  return (
    <Autocomplete
      disabled={companyUuid === null}
      getOptionLabel={driverLabel}
      isOptionEqualToValue={isSameDriver}
      loading={loading}
      loadingText="Loading drivers…"
      noOptionsText="This company has no active drivers."
      options={drivers}
      renderInput={renderInput}
      value={value}
      onChange={handleChange}
    />
  );
};

export default BenchDriverPicker;
